import styled from '@emotion/styled';
import { Divider, ModalProps, Tag } from 'antd';
import SafeImage from 'components/SafeImage';
import StickyModal from 'components/StickyModal';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import ImageSlide from 'views/Review/components/ImageSlide';
import { StarRate } from 'views/Review/components/Profile';
import useTextsTranslate from './useTextsTranslate';
import { uniq } from 'lodash-es';

function ReviewLineModal({
  review,
  ...otherProps
}: ModalProps & {
  review: ReviewAnalysis.Review;
}) {
  const {
    itemInfo: { img, name, sku, sellerSku },
    reviews,
  } = review;

  const texts = useMemo(() => {
    return uniq(reviews.map(({ text }) => text).filter((_) => _));
  }, [reviews]);
  const {
    t,
    trigger: translateTrigger,
    result: translateResult,
    showTranslate,
  } = useTextsTranslate(texts);

  return (
    <StickyModal width={600} title={t('评价轨迹')} footer={false} {...otherProps}>
      <Container>
        <div>
          <SafeImage width={56} height={56} src={img} />
          <section>
            <p>{name}</p>
            <div>
              SKU：
              {sku}
              <Divider type="vertical" />
              {t('sider.SKU货号')}：{sellerSku}
            </div>
          </section>
        </div>
        {texts.length > 0 && translateTrigger}
        <ul>
          {reviews.map((review) => {
            const { id, timestamp, star, commentType, text, images, videos } = review;

            const showText = showTranslate ? translateResult?.[text] || text : text;

            return (
              <li key={id}>
                <section>
                  <div>
                    <span>{dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm')}</span>
                    <StarRate disabled value={star} />
                    {commentType === 'SELLER' && <Tag>{t('回评')}</Tag>}
                  </div>
                </section>
                {showText}
                <ImageSlide reviewDetail={{ imageUrls: images, videoUrls: videos }} />
              </li>
            );
          })}
        </ul>
      </Container>
    </StickyModal>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  > div {
    display: flex;
    padding: 10px 12px;
    margin-bottom: 14px;
    border: solid 1px #f0f2f5;
    > div {
      flex: 0 0 auto;
      margin-right: 10px;
    }
    > section {
      flex: 1 0 0;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      overflow: hidden;
      > p {
        margin-bottom: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      > div {
        font-size: 12px;
        color: #84868c;
      }
    }
  }
  > button {
    align-self: flex-end;
    margin-bottom: 14px;
    padding-right: 0;
  }
  > ul {
    position: relative;
    margin: 0;
    padding: 0;
    list-style: none;
    &::before {
      content: '';
      display: 'inline-block';
      position: absolute;
      top: 0;
      left: 0;
      width: 8px;
      height: 16px;
      background: #fff;
      z-index: 1;
    }
    > li {
      position: relative;
      margin-left: 4px;
      padding-left: 12px;
      padding-bottom: 24px;
      border-left: solid 2px #f0f2f5;
      &:last-of-type::after {
        content: '';
        display: 'inline-block';
        position: absolute;
        top: 23px;
        left: -2px;
        width: 8px;
        height: 100%;
        background: #fff;
        z-index: 1;
      }
      &::before {
        content: '';
        display: inline-block;
        position: absolute;
        top: 13px;
        left: -6px;
        width: 10px;
        height: 10px;
        border: solid 2px var(--ant-primary-color);
        border-radius: 5px;
        background: #fff;
        z-index: 1;
      }
      > section {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 32px;
        > div {
          display: flex;
          align-items: center;
          > span {
            margin-right: 8px;
            margin-top: 4px;
            font-size: 12px;
            color: #84868c;
          }
        }
      }
    }
  }
`;

export default ReviewLineModal;
